/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { gnoment } from 'shared-ui/gnoment';
import { Button, HelpTip, useFlags } from 'sarsaparilla';
import get from 'lodash/get';
import {
    LOTTERY_TYPES,
    DATE_FORMAT,
    RES_ID_DELIMITER,
    DATE_TIME_FORMAT,
    DATE_TITLE_FORMAT,
} from '../utils/constants';

export default function LotteryWidget({
    lotteries = [],
    permit = {},
    enterLottery = () => {},
    isDisabledButton = false,
    isLoadingButton = false,
    isOldLotteryFormat = false,
    isFutureLottery,
    futureLotteryTitle,
    desktopDisabled,
}) {
    const { enableLotteryTransparency } = useFlags();

    const renderStatusBadge = (isInFuture) => {
        if (isInFuture) {
            return <span className="rec-badge-coming-soon">COMING SOON...</span>;
        }
        return <span className="rec-badge-open">OPEN NOW!</span>;
    };

    const currentDate = gnoment();
    const timeZone = permit.time_zone === '' ? 'America/Los_Angeles' : permit.time_zone;

    const lotteriesToRender = lotteries
        .map(({ lottery, type: lotteryType, daily_lottery_target_date: targetDate }) => {
            const {
                id: lotteryId,
                display_at,
                open_at,
                close_at,
                announced_at,
                inventory_info,
            } = lottery;
            const displayTime = isOldLotteryFormat
                ? gnoment(display_at).tz(permit.time_zone)
                : gnoment.unix(display_at.seconds).tz(timeZone);
            const openTime = isOldLotteryFormat
                ? gnoment(open_at).tz(permit.time_zone)
                : gnoment.unix(open_at.seconds).tz(timeZone);
            const closeTime = isOldLotteryFormat
                ? gnoment(close_at).tz(permit.time_zone)
                : gnoment.unix(close_at.seconds).tz(timeZone);
            const announcedTime = isOldLotteryFormat
                ? gnoment(announced_at).tz(permit.time_zone)
                : gnoment.unix(announced_at.seconds).tz(timeZone);
            const lotteryRangeStart = isOldLotteryFormat
                ? gnoment(get(inventory_info, 'dates.start')).tz(permit.time_zone)
                : gnoment.unix(get(inventory_info, 'dates.start.seconds')).utc();
            const lotteryRangeEnd = isOldLotteryFormat
                ? gnoment(get(inventory_info, 'dates.end')).tz(permit.time_zone)
                : gnoment.unix(get(inventory_info, 'dates.end.seconds')).utc();
            const dailyTripDate = gnoment(targetDate);

            if (
                !displayTime?.isValid() ||
                !openTime?.isValid() ||
                !closeTime?.isValid() ||
                !lotteryRangeStart?.isValid() ||
                !lotteryRangeEnd?.isValid() ||
                !announcedTime?.isValid()
            ) {
                return null;
            }
            let daysLeft = closeTime.diff(currentDate, 'days');
            let daysLeftLabel;
            switch (daysLeft) {
                case 0:
                    daysLeftLabel = 'day';
                    daysLeft = 'less than 1';
                    break;
                case 1:
                    daysLeftLabel = 'day';
                    break;
                default:
                    daysLeftLabel = 'days';
                    break;
            }

            const isDailyLottery = isOldLotteryFormat
                ? !!lottery.is_daily
                : lotteryType === LOTTERY_TYPES.Daily;
            const entryDates = lotteryRangeStart.isSame(lotteryRangeEnd)
                ? lotteryRangeStart.format(DATE_FORMAT)
                : `${lotteryRangeStart.format(DATE_FORMAT)} - ${lotteryRangeEnd.format(DATE_FORMAT)}`;
            const isInFuture = currentDate.isBefore(openTime);

            return (
                <div className="rec-lottery-widget-body" key={lotteryId}>
                    <div className="rec-form-item-wrapper-full">
                        <div className="rec-badge-container">
                            {renderStatusBadge(isInFuture)}
                        </div>
                        <div className="rec-badge-title">
                            {get(
                                lottery,
                                'name',
                                `${permit.name} ${isDailyLottery ? 'Daily ' : ''}Lottery`
                            )}
                        </div>
                        {isInFuture && (
                            <div className="rec-badge-title">
                                Opens {openTime.format(DATE_TITLE_FORMAT)}
                            </div>
                        )}
                        <div className="rec-form-item-wrap-full">
                            <div className="rec-data-container">
                                <label htmlFor="rec-lottery-dates">
                                    <b>Event Date(s)</b>
                                    <HelpTip
                                        tipForDescriptor="This lottery allows for opportunities within these dates"
                                        maxWidth={130}
                                        content="This lottery allows for opportunities within these dates"
                                    />
                                </label>
                                <div className="rec-lottery-dates">
                                    {isDailyLottery && dailyTripDate.isValid()
                                        ? dailyTripDate.format(DATE_FORMAT)
                                        : entryDates}
                                </div>
                            </div>
                            <div className="rec-data-container">
                                {!isInFuture && (
                                    <>
                                        <label htmlFor="rec-lottery-dates">
                                            <b>Application Deadline </b>{' '}
                                        </label>
                                        <div className="rec-lottery-dates">
                                            {closeTime.format(DATE_TIME_FORMAT)}
                                            <div className="lottery-days-left">
                                                {daysLeft} {daysLeftLabel} left
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="rec-data-container">
                                <label htmlFor="rec-lottery-dates">
                                    <b>Lottery Results Available</b>
                                </label>
                                <div className="rec-lottery-dates">
                                    {announcedTime.format(DATE_FORMAT)} (Estimated)
                                </div>
                            </div>
                        </div>
                        <div className="rec-form-item-wrap-full">
                            {!isInFuture && desktopDisabled && (
                                <Button
                                    id="disabled-enter-lottery-button"
                                    isDisabled
                                    size="lg"
                                    onClick={() => {}}
                                    shouldFitContainer
                                    className="bottom-2"
                                    type="button"
                                >
                                    Only available on mobile devices
                                </Button>
                            )}
                            {!isInFuture && !desktopDisabled && (
                                <Button
                                    id="enter-lottery-button"
                                    isDisabled={isLoadingButton || isDisabledButton}
                                    isLoading={isLoadingButton}
                                    size="lg"
                                    onClick={() =>
                                        enterLottery(
                                            RES_ID_DELIMITER + permit.id,
                                            lotteryId
                                        )
                                    }
                                    shouldFitContainer
                                    className="bottom-2"
                                    type="button"
                                >
                                    Register for Open Lottery
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            );
        })
        .filter((item) => item);

    return (
        <div className="right-rail-card">
            {futureLotteryTitle && isFutureLottery && (
                <div className="rec-aside-subheader-gray">
                    <div>{futureLotteryTitle}</div>
                </div>
            )}
            {lotteriesToRender}
            {lotteriesToRender?.length > 0 && (
                <div className="rec-form-item-wrap-full">
                    {enableLotteryTransparency ? (
                        <Button
                            appearance="link"
                            shouldFitContainer
                            href="/lottery/available"
                        >
                            Lottery Statistics and Information
                        </Button>
                    ) : (
                        <Button
                            appearance="link"
                            shouldFitContainer
                            href="/lottery/how-they-work"
                        >
                            How Does the Lottery Work?
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
}

LotteryWidget.propTypes = {
    lotteries: PropTypes.arrayOf(PropTypes.object),
    permit: PropTypes.object,
    enterLottery: PropTypes.func,
    isDisabledButton: PropTypes.bool,
    isLoadingButton: PropTypes.bool,
    isOldLotteryFormat: PropTypes.bool,
    isFutureLottery: PropTypes.bool,
    futureLotteryTitle: PropTypes.string,
    desktopDisabled: PropTypes.bool,
};
